/**
 * PCM playback helpers for the in-app audio self-test (Settings → Audio
 * self-test).
 *
 * The self-test captures audio through the EXACT same path a live call
 * uses — `createPcmAudioContext()` + `createPcmCapture()` in
 * `pcmCapture.js`, which run the `pcm-worklet.js` AudioWorklet and emit
 * raw 16-bit signed little-endian mono PCM at the device's native sample
 * rate. Those bytes are byte-identical to what `sendChain.js` streams to
 * the server. To let Bas HEAR what the server receives, we have to turn
 * those same bytes back into something the Web Audio API can play.
 *
 * Why this matters (rule 8 context): the older mic-test panel records via
 * MediaRecorder + Opus, which does its own internal resampling and so was
 * always clean — that's precisely why it couldn't surface the #811-class
 * PCM-path aliasing that garbled real calls. Playing back the real PCM
 * frames is the whole point of the self-test.
 *
 * These helpers are intentionally pure (no Web Audio, no DOM) so they can
 * be unit-tested deterministically — the actual AudioBuffer construction
 * + playback lives in TabletApp.js where the AudioContext is.
 */

/**
 * Convert raw 16-bit signed little-endian PCM into Float32 samples in the
 * [-1, 1) range expected by Web Audio's AudioBuffer.getChannelData().
 *
 * Accepts an ArrayBuffer (the common case — Blob.arrayBuffer() output) or
 * an existing Int16Array. An odd ArrayBuffer byte length (a torn final
 * frame) is tolerated: the trailing odd byte is dropped rather than
 * throwing, because a dropped half-sample is inaudible and we never want
 * the diagnostic to crash on a ragged buffer.
 *
 * Scale: we divide by 32768 (2^15) — the conventional decode constant.
 * The worklet encodes with `sample * 32767`, so the round-trip is off by
 * <0.003%, far below audibility; using 32768 guarantees the result never
 * exceeds 1.0.
 *
 * @param {ArrayBuffer|Int16Array} input
 * @returns {Float32Array}
 */
export function int16ToFloat32(input) {
  let i16;
  if (input instanceof Int16Array) {
    i16 = input;
  } else if (input && typeof input.byteLength === 'number') {
    // ArrayBuffer (or a TypedArray's .buffer). Trim a torn trailing byte
    // so Int16Array construction never throws on an odd length.
    const usableBytes = input.byteLength - (input.byteLength % 2);
    i16 = new Int16Array(input, 0, usableBytes / 2);
  } else {
    return new Float32Array(0);
  }
  const out = new Float32Array(i16.length);
  for (let i = 0; i < i16.length; i++) {
    out[i] = i16[i] / 32768;
  }
  return out;
}

/**
 * Duration in milliseconds of a mono 16-bit PCM buffer at a given sample
 * rate. Used to show the tester how long their recording is.
 *
 * @param {number} byteLength  total PCM bytes (2 bytes per sample)
 * @param {number} sampleRate  samples per second (e.g. 48000)
 * @returns {number} duration in ms, 0 for empty / invalid input
 */
export function pcmDurationMs(byteLength, sampleRate) {
  if (!byteLength || !sampleRate || sampleRate <= 0) return 0;
  const samples = Math.floor(byteLength / 2);
  return Math.round((samples / sampleRate) * 1000);
}

/**
 * getUserMedia audio constraints for the self-test.
 *
 * `raw === false` (default) returns EXACTLY what a live call requests —
 * echoCancellation / noiseSuppression / autoGainControl all on — so the
 * recording faithfully reproduces what the server receives.
 *
 * `raw === true` turns all three off, bypassing the browser's WebRTC voice
 * DSP. That DSP is tuned for telephony (high-pass + spectral subtraction +
 * AGC) and can leave a good wired headset sounding thin / "tinny"; the raw
 * mode lets the tester A/B whether NS/AGC is what dulls the tone vs. the
 * mic itself.
 *
 * @param {boolean} raw
 * @returns {{audio: {echoCancellation:boolean, noiseSuppression:boolean, autoGainControl:boolean}}}
 */
export function selfTestMicConstraints(raw) {
  const on = !raw;
  return { audio: { echoCancellation: on, noiseSuppression: on, autoGainControl: on } };
}
